/*
 * FC Pre-order — split add-to-cart (v3.3, network-level)
 *
 * v3.3: EXCLUDED products (Pre-order Exclusions) are split too, but with
 * HIDDEN-ONLY properties: `_fc_preorder` + `_fc_preorder_note: <note>`
 * (underscore props never show at checkout / order status / emails — only in
 * the admin order view, like the NM app's `_purchase_type`). Shopify Flow
 * still tags off `_fc_preorder`. Non-excluded products keep the visible
 * `Pre-order: <note>` so their customers see the delivery estimate at
 * checkout. Signalled by data-fc-excluded="true" on .fc-preorder-data.
 *
 * SPLIT MODE ONLY (loaded gated on settings.fc_preorder_split_enabled, and
 * self-gated on window.fcPreorderSplit at request time). When a continue-selling
 * variant is added beyond its available stock, the outgoing /cart/add request is
 * REWRITTEN IN FLIGHT into a multi-item add:
 *   - an in-stock line (no properties)
 *   - a pre-order line: hidden `_fc_preorder: true` + visible
 *     `Pre-order: <delivery note>` (shows at checkout / order / notifications;
 *     Shopify Flow tags the order off `_fc_preorder`)
 *
 * WHY the network layer (v3.2) and not the form submit (v3.1): on this store the
 * PDP add is not owned by the theme alone — the subscription widget (an @app
 * block) and the Opus cart app both touch the add flow, so a `submit` listener
 * either never fires (the app does its own AJAX) or is forced to back off by
 * app-injected form inputs. window.fetch is the single choke point every adder
 * goes through; rewriting the payload there covers Dawn, the widget and the app
 * identically. This is the same technique pre-order apps themselves use.
 *
 * Splitting at ADD time (before the cart drawer fetches the cart) is what keeps
 * the third-party (Opus) drawer's stepper state accurate. Later drift (stepper
 * overflow) is converged at page load / checkout intent by ego-custom-v2.js.
 *
 * A request is only rewritten when ALL of these hold — anything else passes
 * through byte-untouched:
 *   - split mode is active (window.fcPreorderTheme + window.fcPreorderSplit)
 *   - POST to /cart/add or /cart/add.js (same origin, any locale prefix)
 *   - the payload is a SINGLE item for the variant described by the PDP's
 *     .fc-preorder-data element (rendered by buy-buttons.liquid)
 *   - no selling plan selected (subscriptions keep their plan untouched)
 *   - no line item properties in the payload (not ours to rewrite)
 *   - the quantity actually overflows the available stock (otherwise no-op)
 *
 * Property keys MUST match ego-custom-v2.js (FC_SPLIT_PROP /
 * FC_SPLIT_VISIBLE_PROP) so add-time and reconciler-created lines merge, and
 * cart templates can suppress the duplicate visible row.
 */
(function () {
  var FC_SPLIT_PROP = '_fc_preorder';
  var FC_SPLIT_VISIBLE_PROP = 'Pre-order';
  var FC_SPLIT_HIDDEN_NOTE_PROP = '_fc_preorder_note';

  function splitActive() {
    try {
      return window.fcPreorderTheme === true && window.fcPreorderSplit === true;
    } catch (e) {
      return false;
    }
  }

  function num(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  // Availability + delivery note for the CURRENT product, server-rendered by
  // buy-buttons.liquid (the Ajax product JSON's inventory_quantity is
  // deprecated, so the DOM element is the only client-side source). Re-queried
  // per request: the element is swapped on variant change (#BuyButtons swap).
  function getPdpData() {
    var el = document.querySelector('.fc-preorder-data');
    if (!el) return null;
    if (el.getAttribute('data-fc-tracked') !== 'true') return null;
    if (el.getAttribute('data-fc-continue') !== 'true') return null;
    var vid = el.getAttribute('data-fc-variant-id');
    if (!vid) return null;
    return {
      variantId: vid,
      available: num(el.getAttribute('data-fc-available')),
      message: el.getAttribute('data-fc-preorder-message') || '',
      excluded: el.getAttribute('data-fc-excluded') === 'true',
    };
  }

  function isCartAddUrl(url) {
    try {
      var u = new URL(url, window.location.origin);
      if (u.origin !== window.location.origin) return false;
      return /\/cart\/add(\.js)?$/.test(u.pathname);
    } catch (e) {
      return false;
    }
  }

  // Normalize the outgoing body (FormData / URLSearchParams / urlencoded string /
  // JSON string) into { id, quantity, blocked, sections, sectionsUrl }.
  // `blocked` = the payload carries something that makes it not ours to rewrite
  // (multi-item add, a selected selling plan, or line item properties).
  function parseAddPayload(body) {
    var out = { id: null, quantity: 1, blocked: false, sections: null, sectionsUrl: null };

    function takeEntry(key, value) {
      var val = String(value == null ? '' : value);
      if (key === 'id') out.id = val;
      else if (key === 'quantity') out.quantity = Math.max(1, num(val));
      else if (key === 'selling_plan' && val.trim() !== '') out.blocked = true;
      else if (key.indexOf('properties[') === 0 && val.trim() !== '') out.blocked = true;
      else if (key === 'sections') out.sections = val;
      else if (key === 'sections_url') out.sectionsUrl = val;
      else if (key === 'items' || key.indexOf('items[') === 0) out.blocked = true;
    }

    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        body.forEach(function (v, k) { takeEntry(k, v); });
        return out;
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        body.forEach(function (v, k) { takeEntry(k, v); });
        return out;
      }
      if (typeof body === 'string') {
        var s = body.trim();
        if (s.charAt(0) === '{') {
          var json = JSON.parse(s);
          var item = json;
          if (Object.prototype.toString.call(json.items) === '[object Array]') {
            if (json.items.length !== 1) { out.blocked = true; return out; }
            item = json.items[0];
          }
          out.id = item.id != null ? String(item.id) : null;
          out.quantity = Math.max(1, num(item.quantity != null ? item.quantity : 1));
          if (item.selling_plan) out.blocked = true;
          if (item.properties && Object.keys(item.properties).length > 0) out.blocked = true;
          if (json.sections) out.sections = String(json.sections);
          if (json.sections_url) out.sectionsUrl = String(json.sections_url);
          return out;
        }
        new URLSearchParams(s).forEach(function (v, k) { takeEntry(k, v); });
        return out;
      }
    } catch (e) {
      /* unparseable → not ours */
    }
    return null;
  }

  // Quantity of this variant already in the cart on PLAIN lines only —
  // `_fc_preorder` lines are already pre-orders and must not eat into the
  // available stock that the in-stock part of this add can still use.
  function plainQtyInCart(origFetch, variantId) {
    return origFetch
      .call(window, '/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sum = 0;
        (cart.items || []).forEach(function (it) {
          if (String(it.variant_id) !== String(variantId)) return;
          if (it.selling_plan_allocation) return;
          var props = it.properties || {};
          if (props[FC_SPLIT_PROP]) return;
          sum += it.quantity;
        });
        return sum;
      })
      .catch(function () { return 0; });
  }

  var origFetch = window.fetch;
  if (typeof origFetch !== 'function' || origFetch.__fcSplitPatched) return;

  function patchedFetch(input, init) {
    var self = this;
    var args = arguments;
    try {
      if (!splitActive()) return origFetch.apply(self, args);

      var url = typeof input === 'string' ? input : input && input.url;
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'POST' || !url || !isCartAddUrl(url)) return origFetch.apply(self, args);

      // A Request object carrying its own body stream can't be inspected without
      // consuming it — pass through untouched (native behaviour preserved).
      var body = init && init.body;
      if (body == null) return origFetch.apply(self, args);

      var data = getPdpData();
      if (!data) return origFetch.apply(self, args);

      var parsed = parseAddPayload(body);
      if (!parsed || parsed.blocked || String(parsed.id) !== String(data.variantId)) {
        return origFetch.apply(self, args);
      }

      var qty = parsed.quantity;
      return plainQtyInCart(origFetch, data.variantId).then(function (inCartNow) {
        var remaining = Math.max(0, data.available - inCartNow);
        var addInStock = Math.min(qty, remaining);
        var addPreorder = qty - addInStock;
        if (addPreorder <= 0) return origFetch.apply(self, args); // fits in stock → untouched

        var items = [];
        if (addInStock > 0) items.push({ id: num(data.variantId), quantity: addInStock });
        var props = {};
        props[FC_SPLIT_PROP] = 'true';
        // Excluded: the note goes in a HIDDEN property (admin-only) — nothing
        // pre-order-related may reach the customer, checkout included.
        if (data.excluded) props[FC_SPLIT_HIDDEN_NOTE_PROP] = data.message || 'Pre-order';
        else props[FC_SPLIT_VISIBLE_PROP] = data.message || 'Pre-order';
        items.push({ id: num(data.variantId), quantity: addPreorder, properties: props });

        var newBody = { items: items };
        if (parsed.sections) {
          newBody.sections = parsed.sections;
          newBody.sections_url = parsed.sectionsUrl || window.location.pathname;
        }

        var newInit = {};
        for (var k in init) newInit[k] = init[k];
        newInit.method = 'POST';
        newInit.body = JSON.stringify(newBody);
        var headers = new Headers((init && init.headers) || {});
        headers.set('Content-Type', 'application/json');
        headers.set('Accept', 'application/json');
        newInit.headers = headers;

        try {
          console.debug('[FC split-add] rewrote /cart/add:', addInStock, 'in stock +', addPreorder, 'pre-order');
        } catch (e) {}
        return origFetch.call(self, url, newInit);
      });
    } catch (e) {
      return origFetch.apply(self, args);
    }
  }
  patchedFetch.__fcSplitPatched = true;
  window.fetch = patchedFetch;
})();
